import React, { useEffect, useState, useRef } from "react";

import styled, { keyframes } from "styled-components";
import { Container, Row, Col } from "react-bootstrap";
import ArrowCircleLeftIcon from "@mui/icons-material/ArrowCircleLeft";

import { D1, GradientSpan, P3 } from "../../../components/theme/text/Headings";

import {
  Blue,
  White,
  gradientColors,
  Purple,
  Black,
  White05,
  Pink,
} from "../../../components/theme/colors";
import { ButtonGradient } from "../../../components/theme/Button";
import { useAuth } from "../../../services/AuthContext";
import { db } from "../../../services/firebase.config";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import mixpanelService from "../../../services/MixpanelService";
import postHogService from "../../../services/PostHogService";
import {
  ProgressBarContainer,
  ProgressBarProgress,
} from "../../../components/theme/QuizBox";
import LayoutOnboarding from "../../../components/layout/layoutOnboarding";
import "bootstrap/dist/css/bootstrap.min.css";

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const TopContent = styled.div`
  margin-top: 6vh;
  padding: 0 15px;
  animation: ${fadeInUp} 0.7s ease-out;
`;

const HeaderRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  min-height: 48px;
  padding: 0px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const BottomLogin = styled.div`
  position: absolute;
  bottom: 10vh;
  left: 2vh;
  right: 2vh;
`;

const PickerContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  height: 200px;
  margin: 20px 0;
  position: relative;

  &::before,
  &::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    height: 60px;
    pointer-events: none;
    z-index: 1;
  }

  &::before {
    top: 0;
    background: linear-gradient(to bottom, ${Black}, transparent);
  }

  &::after {
    bottom: 0;
    background: linear-gradient(to top, ${Black}, transparent);
  }
`;

const PickerColumn = styled.div`
  flex: 1;
  overflow-y: auto;
  scroll-snap-type: y mandatory;
  padding: 80px 0;
  position: relative;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const PickerItem = styled.div`
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  scroll-snap-align: center;
  transition: all 0.2s ease;
  color: ${(props) => (props.$selected ? White : "rgba(255, 255, 255, 0.4)")};
  font-size: ${(props) => (props.$selected ? "20px" : "16px")};
  font-weight: ${(props) => (props.$selected ? "700" : "400")};
  font-family: "Satoshi-Variable", sans-serif;
`;

const SelectionHighlight = styled.div`
  position: absolute;
  left: 10px;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  height: 40px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  pointer-events: none;
`;

const OnboardingOneFive = () => {
  const navigate = useNavigate();
  const { currentUserState } = useAuth();
  const [birthday, setBirthday] = useState("");
  const [error, setError] = useState("");

  // Track page view on mount
  useEffect(() => {
    const props = {
      user_id: currentUserState?.uid || "anonymous",
      step: "birthday",
      timestamp: new Date().toISOString(),
    };
    mixpanelService.track("Onboarding Birthday Viewed", props);
    postHogService.track("Onboarding Birthday Viewed", props);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Picker state
  const currentYear = new Date().getFullYear();
  const [selectedMonth, setSelectedMonth] = useState(0);
  const [selectedDay, setSelectedDay] = useState(1);
  const [selectedYear, setSelectedYear] = useState(currentYear - 25);

  const monthRef = useRef(null);
  const dayRef = useRef(null);
  const yearRef = useRef(null);

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const years = Array.from({ length: 120 }, (_, i) => currentYear - i);
  const getDaysInMonth = (month, year) =>
    new Date(year, month + 1, 0).getDate();
  const days = Array.from(
    { length: getDaysInMonth(selectedMonth, selectedYear) },
    (_, i) => i + 1
  );

  useEffect(() => {
    const fetchProfileData = async () => {
      if (!currentUserState) return;

      const profileDocRef = doc(db, "public-profiles", currentUserState.uid);
      const profileDoc = await getDoc(profileDocRef);

      if (profileDoc.exists()) {
        const data = profileDoc.data();
        if (data.birthday) {
          // Convert timestamp to date string for input field
          const date = data.birthday.toDate
            ? data.birthday.toDate()
            : new Date(data.birthday);
          setBirthday(date.toISOString().split("T")[0]);
        }
      }
    };

    fetchProfileData();
  }, [currentUserState]);

  const handleScroll = (ref, setter, items) => {
    if (!ref.current) return;
    const scrollTop = ref.current.scrollTop;
    const itemHeight = 40;
    const index = Math.round(scrollTop / itemHeight);
    setter(items[index]);
  };

  const scrollToIndex = (ref, index) => {
    if (!ref.current) return;
    const itemHeight = 40;
    ref.current.scrollTo({
      top: index * itemHeight,
      behavior: "smooth",
    });
  };

  // Update birthday whenever picker values change
  useEffect(() => {
    const month = String(selectedMonth + 1).padStart(2, "0");
    const day = String(selectedDay).padStart(2, "0");
    const dateString = `${selectedYear}-${month}-${day}`;
    setBirthday(dateString);
    setError("");
  }, [selectedMonth, selectedDay, selectedYear]);

  // Initialize picker positions on mount
  useEffect(() => {
    setTimeout(() => {
      scrollToIndex(monthRef, selectedMonth);
      scrollToIndex(dayRef, selectedDay - 1);
      scrollToIndex(yearRef, years.indexOf(selectedYear));
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const calculateAge = (birthDate) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birth.getDate())
    ) {
      age--;
    }

    return age;
  };

  const validateBirthday = (birthDate) => {
    if (!birthDate) {
      setError("Please enter your birthday");
      return false;
    }

    const age = calculateAge(birthDate);
    if (age > 120) {
      setError("Please enter a valid birthday");
      return false;
    }

    setError("");
    return true;
  };

  const handleBack = async () => {
    navigate("/onboarding-one-twenty-five");
  };

  const handleUpdateProfile = async () => {
    if (!currentUserState) {
      alert("You need to be logged in to update your profile");
      return;
    }

    if (!validateBirthday(birthday)) {
      return;
    }

    const age = calculateAge(birthday);

    // Add age confirmation alert
    const confirmed = window.confirm(
      `Please confirm you are ${age} years old.`
    );
    if (!confirmed) {
      return;
    }

    const profileDocRef = doc(db, "public-profiles", currentUserState.uid);

    // Create a local date object to avoid timezone issues
    const createLocalDate = (dateString) => {
      const [year, month, day] = dateString.split("-").map(Number);
      return new Date(year, month - 1, day); // month is 0-indexed in JavaScript
    };

    try {
      if (age < 17) {
        // Set flag for under-17 users and redirect to age restriction page
        await setDoc(
          profileDocRef,
          {
            uid: currentUserState.uid,
            birthday: createLocalDate(birthday),
            isUnder17: true,
            ageRestrictedAt: new Date(),
          },
          { merge: true }
        );

        navigate("/age-restriction");
        return;
      }

      // Normal flow for users 17+
      await setDoc(
        profileDocRef,
        {
          uid: currentUserState.uid,
          birthday: createLocalDate(birthday),
          isUnder17: false,
        },
        { merge: true }
      );

      navigate("/onboarding-two");
    } catch (error) {
      console.error("Error adding birthday: ", error);
      alert("There was an error saving your birthday. Please try again.");
    }
  };

  return (
    <LayoutOnboarding>
      <ProgressBarContainer style={{ marginBottom: 8 }}>
        <ProgressBarProgress
          $background={Blue}
          $transitionBackground={Purple}
          width={`50%`}
        />
      </ProgressBarContainer>
      <HeaderRow>
        <div
          style={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            width: "40px",
            height: "40px",
          }}
          onClick={handleBack}
        >
          <ArrowCircleLeftIcon
            style={{
              color: White05,
              fontSize: "28px",
            }}
          />
        </div>
      </HeaderRow>
      <Container>
        <Row>
          <Col
            lg={{ offset: 4, span: 4 }}
            md={{ offset: 3, span: 6 }}
            sm={{ offset: 2, span: 8 }}
            xs={{ span: 12 }}
            className="text-align-center"
          >
            <TopContent>
              <D1 color={White} className="mt-40 mb-50">
                When's your{" "}
                <GradientSpan $background={Pink} $transitionBackground={Blue}>
                  birthday?
                </GradientSpan>
              </D1>
              <PickerContainer>
                <SelectionHighlight />

                <PickerColumn
                  ref={monthRef}
                  onScroll={() =>
                    handleScroll(
                      monthRef,
                      setSelectedMonth,
                      months.map((_, i) => i)
                    )
                  }
                >
                  {months.map((month, index) => (
                    <PickerItem
                      key={month}
                      $selected={selectedMonth === index}
                      onClick={() => {
                        setSelectedMonth(index);
                        scrollToIndex(monthRef, index);
                      }}
                    >
                      {month}
                    </PickerItem>
                  ))}
                </PickerColumn>

                <PickerColumn
                  ref={dayRef}
                  onScroll={() => handleScroll(dayRef, setSelectedDay, days)}
                >
                  {days.map((day) => (
                    <PickerItem
                      key={day}
                      $selected={selectedDay === day}
                      onClick={() => {
                        setSelectedDay(day);
                        scrollToIndex(dayRef, day - 1);
                      }}
                    >
                      {day}
                    </PickerItem>
                  ))}
                </PickerColumn>

                <PickerColumn
                  ref={yearRef}
                  onScroll={() => handleScroll(yearRef, setSelectedYear, years)}
                >
                  {years.map((year, index) => (
                    <PickerItem
                      key={year}
                      $selected={selectedYear === year}
                      onClick={() => {
                        setSelectedYear(year);
                        scrollToIndex(yearRef, index);
                      }}
                    >
                      {year}
                    </PickerItem>
                  ))}
                </PickerColumn>
              </PickerContainer>
              {error && (
                <P3 color="red" className="mt-10">
                  {error}
                </P3>
              )}
            </TopContent>
            <BottomLogin>
              <div style={{ padding: "0 15px" }}>
                <ButtonGradient
                  width="100%"
                  className="mt-20"
                  $background={gradientColors["personality-check"].background}
                  $transitionBackground={
                    gradientColors["personality-check"].transitionBackground
                  }
                  onClick={handleUpdateProfile}
                >
                  Continue
                </ButtonGradient>
              </div>
            </BottomLogin>
          </Col>
        </Row>
      </Container>
    </LayoutOnboarding>
  );
};

export default OnboardingOneFive;
